import { type VariantProps } from "class-variance-authority";
import { forwardRef, useMemo } from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { buttonVariants } from "./styles";
import { Spinner } from "../spinner";

export interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  hoverLabel?: string;
  loading?: boolean;
  asChild?: boolean;
  // Allow any data-* or aria-* attributes
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | number | boolean | undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size, hoverLabel, loading, asChild, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const resolvedProps = useMemo<typeof props>(
      () => (Comp === "button" ? { type: "button", ...props } : props),
      [Comp, props]
    );

    const buttonEl = (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          hoverLabel && props.disabled && "disabled:pointer-events-auto"
        )}
        {...resolvedProps}
        disabled={loading || props.disabled}
      >
        {!!loading && <Spinner size="xs" color={variant === "default" ? "white" : "secondary"} />}
        <Slottable>{children}</Slottable>
      </Comp>
    );

    if (hoverLabel) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{buttonEl}</TooltipTrigger>
            <TooltipContent>{hoverLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return buttonEl;
  }
);
Button.displayName = "Button";
